import { toast } from 'react-toastify';
import CopyToClipboardButton from '../components/copy-button';
import RandomImage from '../components/random-image';
import { v4 as uuid } from 'uuid';
import { IoClose } from 'react-icons/io5';

//styles
const randomImageStyle = {
  position: 'absolute',
  height: 70,
  right: 0,
  top: 0,
  opacity: 0.3,
  zIndex: -1,
};

// Default settings for toast notifications
const defaultSettings = {
  position: 'bottom-left',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: 'colored',
  update: true,
  icon: false,
};

// Custom close button component for toasts
const CloseButton = ({ closeToast }) => (
  <i
    data-testid='close-button'
    className='Toastify__close-button'
    style={{ height: 15, width: 15 }}
    onClick={closeToast}>
    <IoClose />
  </i>
);

// Default settings for error toasts
const defaultErrorOptions = {
  allowCopy: true,
  closeButton: CloseButton,
  closeOnClick: false,
  autoClose: true,
  ...defaultSettings,
};

// Types of toasts
const toastTypes = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  MESSAGE: 'message',
};

// Function to calculate the width of a text string
function getTextWidth(text, font) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = font || getComputedStyle(document.body).font;
    return context.measureText(text).width;
  }
  return 0;
}

// Function to calculate the width of an error message
function getErrorWidth(text, title = null, copyToClipboard) {
  const isArray = Array.isArray(text);
  if (!isArray) {
    return text;
  }
  const maxWidth = 800;
  const width = text.reduce(function (width, errorMessage) {
    const nextWidth = getTextWidth(errorMessage, '20px Roboto sans-serif');
    if (width < nextWidth) {
      return Math.min(nextWidth, maxWidth);
    }
    return width;
  }, 320);
  const titleWidth = title ? getTextWidth(title, '24px sans-serif') : 0;
  return {
    width: `${
      titleWidth > width ? titleWidth : width + (copyToClipboard ? 50 : 0)
    }px`,
  };
}

// Function to determine the display color based on message types
function getDisplayColor(messages) {
  const hasError = messages.some((m) => m.type === toastTypes.ERROR);
  const hasWarning = messages.some((m) => m.type === toastTypes.WARNING);
  const hasMessage = messages.some((m) => m.type === toastTypes.MESSAGE);
  if (hasError) {
    return 'error';
  }
  if (hasWarning) {
    return 'warning';
  }
  if (hasMessage) {
    return 'info';
  }
  return 'white';
}

// Function to render the messages in the toast
function renderMessages(messages, title, copyToClipboard, color, closeToast) {
  const render = (
    <div className='toastContainer' style={{ overflow: 'hidden !important', position: 'relative' }}>
      {/* single decorative image positioned relative to the toast container */}
      <RandomImage style={randomImageStyle} />
      <div>
        <div>
          {title ? (
            <span>
              <h5
                style={{
                  marginBottom: messages.length === 0 ? '0' : '8px',
                  color: `var(--${color})`,
                }}>
                {title}
              </h5>
            </span>
          ) : null}
          <div className="centre-scroll" style={{ maxHeight: 200, overflowY: 'auto', paddingRight: 44 }}>
            {messages.map((e, index) => {
              if (!e.text) {
                /* istanbul ignore next */
                return null;
              }
              return (
                <div
                  key={index}
                  style={{ display: 'flex', alignItems: 'flex-start', paddingBottom: 6 }}>
                  <div style={{ marginLeft: 8, whiteSpace: 'pre-wrap' }}>{e.text}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {copyToClipboard && (
        <div className="copy-toast-button">
          <CopyToClipboardButton
            text={messages.map((m) => `[${m.type}]: ${m.text}`).join('\n')}
            // use the closeToast passed from the toast creator (scoped to the toastId)
            closeToast={closeToast}
          />
        </div>
      )}
    </div>
  );
  return render;
}

function createToastService() {
  // Function to display a success toast
  function success(text, toastId = uuid(), options = defaultSettings) {
    return displayToast({
      toastId,
      data: { message: text },
      type: toastTypes.SUCCESS,
      options,
    });
  }

  // Function to display an error toast
  function error(text, toastId = uuid(), options = defaultErrorOptions) {
    return displayToast({
      toastId,
      data: { message: text },
      type: toastTypes.ERROR,
      options,
    });
  }

  // Function to display a warning toast
  function warn(
    text,
    toastId = uuid(),
    options = defaultErrorOptions,
    title = null
  ) {
    return displayToast({
      toastId,
      title,
      data: { message: text },
      type: toastTypes.WARNING,
      options,
    });
  }

  // Function to display an info toast
  function info(
    text,
    toastId = uuid(),
    options = defaultSettings,
    title = null
  ) {
    return displayToast({
      toastId,
      data: { message: text },
      title,
      type: toastTypes.INFO,
      options,
    });
  }

  // Function to display a toast with custom options
  function displayToast({
    toastId,
    data,
    title,
    type,
    options = {},
    copyToClipboard,
  }) {
    let messages = [];
    let errors = false;
    let header = title || options.title;
    if (typeof data === 'object') {
      // Collect messages from different possible shapes: string, array, or object mapping field -> array
      const collected = [];

      // If data is an array (e.g., API returned errors: ["...","..."] and we passed that array as data),
      // map each item to an individual message so they render on their own lines.
      if (Array.isArray(data)) {
        data.forEach((item) => {
          if (item) collected.push({ type, text: item });
        });
      } else {
        if (data.error) collected.push({ type: toastTypes.ERROR, text: data.error });

        const collectField = (field, msgType) => {
          const val = data[field];
          if (!val) return;
          if (Array.isArray(val)) {
            val.forEach((item) => {
              if (item) collected.push({ type: msgType, text: item });
            });
          } else if (typeof val === 'object') {
            // object mapping keys to arrays or single messages
            Object.values(val).forEach((v) => {
              if (Array.isArray(v)) {
                v.forEach((item) => {
                  if (item) collected.push({ type: msgType, text: item });
                });
              } else if (v) {
                collected.push({ type: msgType, text: v });
              }
            });
          } else {
            collected.push({ type: msgType, text: val });
          }
        };

        collectField('errors', toastTypes.ERROR);
        collectField('warnings', toastTypes.WARNING);
        collectField('messages', toastTypes.MESSAGE);

        // Fallback: if no collected messages but a top-level message exists, use it
        let usedFallback = false;
        if (collected.length === 0 && data.message) {
          usedFallback = true;
          const m = data.message;
          if (Array.isArray(m)) {
            m.forEach((item) => item && collected.push({ type, text: item }));
          } else {
            collected.push({ type, text: m });
          }
        }

        // If collected came from fields, prefer header from data.message when present
        if (!usedFallback && collected.length > 0) {
          header = data.message || header;
        }

        errors = data.success;
      }

      // Deduplicate by text while preserving order
      const seen = new Set();
      messages = collected.filter((m) => m.text).filter((m) => {
        if (seen.has(m.text)) return false;
        seen.add(m.text);
        return true;
      });

      if (messages.length === 0) {
        /* istanbul ignore next */
        messages = [{ type, text: data }].filter((m) => m.text);
      }
    } else {
      /* istanbul ignore next */
      messages = [{ type, text: data }];
    }

    const width = getErrorWidth(
      messages.slice(0, 5).map((m) => m.text),
      header,
      copyToClipboard
    );
    const autoClose = errors && messages.length > 1 ? false : options.autoClose;
    const fullOptions = {
      ...defaultErrorOptions,
      ...options,
      copyToClipboard,
      style: width,
      autoClose: !autoClose
        ? autoClose
        : autoClose || defaultErrorOptions.autoClose,
      isLoading: false,
    };

    if (type) {
      fullOptions.type = type;
    }
    // create a close function scoped to this toastId so UI elements can dismiss this toast
    const closeThisToast = () => {
      try {
        if (toast && toast.dismiss) {
          toast.dismiss(toastId);
        }
      } catch (e) {
        /* istanbul ignore next */
      }
    };

    const isActive = toast.isActive ? toast.isActive(toastId) : false;
    if (isActive) {
      return toast.update(toastId, {
        ...fullOptions,
        render: renderMessages(
          messages,
          header,
          copyToClipboard,
          getDisplayColor(messages),
          closeThisToast
        ),
      });
    }
    const toaster = renderMessages(
      messages,
      header,
      copyToClipboard,
      getDisplayColor(messages),
      closeThisToast
    );
    return toast(toaster, {
      ...defaultSettings,
      toastId,
      ...fullOptions,
      type,
    });
  }

  return {
    success,
    warn,
    error,
    info,
    displayToast,
    toastTypes,
  };
}

// Export the factory function
export { createToastService };

// Export a singleton instance as default for backward compatibility
const toastService = createToastService();
export default toastService;
